/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import _ from 'lodash';
import { Header, Icon, Segment, Popup } from 'semantic-ui-react';

/**
 * Continuous gradient colour based on budget usage percentage.
 * 0% → green (hsl 130, 55%, 35%)  →  100% → red (hsl 0, 75%, 45%)
 * Over 100% → dark red.
 */
function getHeatColor(percentUsed) {
  if (percentUsed > 100) return 'hsl(0, 80%, 28%)';
  const clamped = Math.max(0, Math.min(percentUsed, 100));
  // Hue: 130 (green) → 0 (red)
  const hue = 130 - (clamped / 100) * 130;
  // Saturation: 55% → 75%
  const sat = 55 + (clamped / 100) * 20;
  // Lightness: 38% → 42% (slight bump mid-range for readability)
  const light = 38 + (clamped / 100) * 4;
  return `hsl(${hue.toFixed(0)}, ${sat.toFixed(0)}%, ${light.toFixed(0)}%)`;
}

function getProjectCode(projectId) {
  if (!projectId) return '';
  const parts = projectId.split('-');
  return parts[0] || projectId;
}

function getTextColor(percentUsed) {
  // White text works well on the saturated gradient at all points
  return percentUsed >= 25 ? '#FFFFFF' : '#000000';
}

const tileStyle = {
  width: '110px',
  height: '72px',
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const tileHoverStyle = {
  transform: 'scale(1.06)',
  boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
};

class BudgetHeatMap extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hoveredTile: null };
  }

  handleMouseEnter = (projectId) => this.setState({ hoveredTile: projectId });
  handleMouseLeave = () => this.setState({ hoveredTile: null });

  renderLegend() {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', fontSize: '0.78em', color: '#4b5563' }}>
        <span>0%</span>
        <div style={{
          flex: 1,
          height: '10px',
          borderRadius: '5px',
          background: `linear-gradient(to right, ${getHeatColor(0)}, ${getHeatColor(25)}, ${getHeatColor(50)}, ${getHeatColor(75)}, ${getHeatColor(100)})`,
        }} />
        <span>100%</span>
        <div style={{
          width: '10px',
          height: '10px',
          borderRadius: '3px',
          backgroundColor: getHeatColor(101),
          marginLeft: '4px',
        }} />
        <span>&gt;100%</span>
      </div>
    );
  }

  renderTile(projectId, budgetData) {
    const { budgetUsage, budgetLimit, budgetForecast, description } = budgetData;
    const { hoveredTile } = this.state;
    const projectCode = getProjectCode(projectId);
    const isHovered = hoveredTile === projectId;

    // Missing budget
    if (budgetLimit === -1 || budgetUsage === -1) {
      return (
        <Popup
          key={projectId}
          trigger={
            <div
              style={{ ...tileStyle, backgroundColor: '#d1d5db', color: '#374151', ...(isHovered ? tileHoverStyle : {}) }}
              onMouseEnter={() => this.handleMouseEnter(projectId)}
              onMouseLeave={this.handleMouseLeave}
            >
              <div style={{ fontSize: '0.8em', fontWeight: '600' }}>{projectCode}</div>
              <div style={{ fontSize: '1.15em', fontWeight: '700', marginTop: '2px' }}>—</div>
            </div>
          }
          content={<div><strong>{projectId}</strong><br />{description && <span>{description}<br /></span>}<span style={{ color: '#dc2626' }}>Budget not configured</span></div>}
          position="top center"
          size="small"
        />
      );
    }

    const usageVal = parseFloat(budgetUsage);
    const limitVal = parseFloat(budgetLimit);
    const forecastVal = budgetForecast ? parseFloat(budgetForecast) : null;
    if (isNaN(usageVal) || isNaN(limitVal)) return null;

    const pct = (usageVal / limitVal) * 100;
    const pctRemaining = 100 - pct;
    const bgColor = getHeatColor(pct);
    const textColor = getTextColor(pct);

    const fmt = (v) => new Intl.NumberFormat().format(v.toFixed(2));

    return (
      <Popup
        key={projectId}
        trigger={
          <div
            style={{ ...tileStyle, backgroundColor: bgColor, color: textColor, ...(isHovered ? tileHoverStyle : {}) }}
            onMouseEnter={() => this.handleMouseEnter(projectId)}
            onMouseLeave={this.handleMouseLeave}
          >
            <div style={{ fontSize: '0.8em', fontWeight: '600' }}>{projectCode}</div>
            <div style={{ fontSize: '1.15em', fontWeight: '700', marginTop: '2px' }}>{pct.toFixed(0)}%</div>
            <div style={{ fontSize: '0.7em', marginTop: '1px', opacity: 0.85 }}>${fmt(usageVal)}</div>
          </div>
        }
        content={
          <div>
            <strong>{projectId}</strong>
            {description && <span> — {description}</span>}
            <br /><br />
            <strong>Budget:</strong> US${fmt(limitVal)}<br />
            <strong>Used:</strong> US${fmt(usageVal)} ({pct.toFixed(2)}%)<br />
            <strong>Remaining:</strong> US${fmt(limitVal - usageVal)} (
              <span style={{ color: pctRemaining <= 10 ? '#dc2626' : 'inherit' }}>{pctRemaining.toFixed(2)}%</span>
            )
            {forecastVal && !isNaN(forecastVal) && (
              <><br /><strong>Forecast:</strong> US${fmt(forecastVal)}{forecastVal > limitVal && <span style={{ color: '#dc2626' }}> — Exceeds budget!</span>}</>
            )}
          </div>
        }
        position="top center"
        size="small"
      />
    );
  }

  render() {
    const { budgets, hideTitle } = this.props;
    if (_.isEmpty(budgets)) return null;

    const projectIds = Object.keys(budgets).sort((a, b) => {
      const aL = budgets[a].budgetLimit;
      const bL = budgets[b].budgetLimit;
      if (aL === -1) return 1;
      if (bL === -1) return -1;
      return (parseFloat(budgets[b].budgetUsage) / parseFloat(bL)) - (parseFloat(budgets[a].budgetUsage) / parseFloat(aL));
    });

    // When used inside the Dashboard widget card, render content directly (no extra wrapper)
    if (hideTitle) {
      return (
        <div>
          {this.renderLegend()}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {projectIds.map(id => this.renderTile(id, budgets[id]))}
          </div>
          <div style={{ fontSize: '0.78em', color: '#9ca3af', marginTop: '12px' }}>
            Hover over tiles for details. Sorted by usage (highest first).
          </div>
        </div>
      );
    }

    // Standalone usage (outside dashboard) — keeps the old wrapper
    return (
      <div className="mb3">
        <div className="mb2 flex">
          <Header as="h3" className="color-rmit-blue mt1 mb0 flex-auto">
            <Icon name="th" className="align-top" />
            <Header.Content className="left-align">Budget Heatmap</Header.Content>
          </Header>
        </div>
        <Segment style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          {this.renderLegend()}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '4px 0' }}>
            {projectIds.map(id => this.renderTile(id, budgets[id]))}
          </div>
          <div style={{ fontSize: '0.78em', color: '#9ca3af', marginTop: '12px', textAlign: 'center' }}>
            Hover over tiles for details. Sorted by usage (highest first).
          </div>
        </Segment>
      </div>
    );
  }
}

export default BudgetHeatMap;
